import type { UseQueryOptions } from '@tanstack/react-query';

import { schedulingListScheduledApps } from '@crossnokaye/typescript-sdk/funcs/schedulingListScheduledApps';
import { ScheduledApp3 } from '@crossnokaye/typescript-sdk/models/components';
import { SDKError } from '@crossnokaye/typescript-sdk/models/errors';
import { Kind } from '@crossnokaye/typescript-sdk/models/operations';
import {
  ListScheduledAppsRequest,
  ListScheduledAppsResponse,
} from '@crossnokaye/typescript-sdk/models/operations';

import { AtlasClientFunction } from '@/client/types';
import { AtlasQueryWithOrgIdAndAgentId } from '@/queries/types';

const transformListScheduledAppsResponse = (
  response: ListScheduledAppsResponse
): ScheduledApp3[] | undefined => {
  return response.scheduledApp3s;
};

export interface ListScheduledAppsQueryProps extends AtlasQueryWithOrgIdAndAgentId {
  client: AtlasClientFunction;
  kind?: Kind;
  transform?: (response: ListScheduledAppsResponse) => ScheduledApp3[] | undefined;
}

// Single source of truth for the query key shape. Mutations that need to
// invalidate scheduled-app lists import this helper instead of hand-rolling
// the tuple — keeps invalidation in lockstep with the factory.
export const listScheduledAppsQueryKey = (
  orgId: string | undefined,
  agentId: string | undefined,
  kind?: Kind
) => [orgId, agentId, 'listScheduledApps', kind ?? 'all'] as const;

export const listScheduledAppsQuery = ({
  orgId,
  agentId,
  client,
  kind,
  transform = transformListScheduledAppsResponse,
}: ListScheduledAppsQueryProps): UseQueryOptions<
  ListScheduledAppsResponse,
  SDKError,
  ScheduledApp3[] | undefined
> => {
  return {
    enabled: !!orgId && !!agentId,
    queryKey: listScheduledAppsQueryKey(orgId, agentId, kind),
    queryFn: () =>
      client<ListScheduledAppsRequest, ListScheduledAppsResponse>({
        atlasFunc: schedulingListScheduledApps,
        request: { orgId: orgId!, agentId: agentId!, kind },
      }),
    select: transform,
  };
};
